import React from 'react';
import { FaFileExcel, FaSearch } from 'react-icons/fa';
import CustomDatePicker from '../../../../../../components/common/CustomDatePicker/CustomDatePicker';
import CheckboxMultiSelect from '../../../../../../components/common/CheckboxMultiSelect/CheckboxMultiSelect';
import { MPSwitch } from '../../../../../../components/ui';

export interface FilterOption {
  value: any;
  label: string;
}

interface FilterBarProps {
  // Date filters
  startDate: string;
  endDate: string;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  
  // Session filter
  soloSesionActual: boolean;
  onSoloSesionActualChange: (enabled: boolean) => void;
  
  // Dropdown filters
  usersOptions: FilterOption[];
  selectedUsers: FilterOption[];
  onUsersChange: (users: FilterOption[]) => void;
  
  mesasOptions: FilterOption[];
  selectedMesas: FilterOption[];
  onMesasChange: (mesas: FilterOption[]) => void;
  
  tipoOptions: FilterOption[];
  selectedTipo: FilterOption[];
  onTipoChange: (tipo: FilterOption[]) => void;
  
  // Product filter
  productosOptions: FilterOption[];
  selectedProductos: FilterOption[];
  onProductosChange: (productos: FilterOption[]) => void;
  
  // Excel export
  onExportExcel: () => void;
  loading?: boolean;
  dataCount?: number;

  // Search props
  searchValue?: string;
  onSearchChange?: (value: string) => void;
}

const FilterBar: React.FC<FilterBarProps> = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  soloSesionActual,
  onSoloSesionActualChange,
  usersOptions,
  selectedUsers,
  onUsersChange,
  mesasOptions,
  selectedMesas,
  onMesasChange,
  tipoOptions,
  selectedTipo,
  onTipoChange,
  productosOptions,
  selectedProductos,
  onProductosChange,
  onExportExcel,
  loading = false,
  dataCount = 0,
  searchValue = '',
  onSearchChange,
}) => {
  return (
    <div className="hidden lg:block mb-3">
      <div className="mp-toolbar">
        {/* Row 1: Search + Date pickers + Session toggle + Export */}
        <div className="flex flex-wrap items-center gap-2 lg:gap-3">
          {/* Search Input */}
          {onSearchChange && (
            <div className="flex-1 min-w-[180px] lg:min-w-[220px] max-w-[380px] relative">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-xs opacity-40" style={{ color: 'var(--text-color)' }} />
              <input
                type="text"
                placeholder="Buscar pedidos..."
                value={searchValue}
                onChange={(e) => onSearchChange(e.target.value)}
                className="mp-form-input"
                style={{ paddingLeft: '2rem' }}
              />
            </div>
          )}

          {/* Date pickers + Session toggle */}
          <div className="flex items-center gap-2">
            <div className="min-w-[145px]">
              <CustomDatePicker value={startDate} onChange={onStartDateChange} disabled={soloSesionActual} placeholder="Desde" />
            </div>
            <div className="min-w-[145px]">
              <CustomDatePicker value={endDate} onChange={onEndDateChange} disabled={soloSesionActual} placeholder="Hasta" />
            </div>
            <MPSwitch label="Sesión actual" checked={soloSesionActual} onChange={() => onSoloSesionActualChange(!soloSesionActual)} />
          </div>

          {/* Export */}
          <div className="ml-auto">
            <button className="mp-btn-export" onClick={onExportExcel} disabled={loading || dataCount === 0}>
              <FaFileExcel />
              <span>Exportar</span>
            </button>
          </div>
        </div>

        {/* Row 2: Dropdown filters — separated by subtle border */}
        <div className="flex items-center gap-2 mt-3 pt-3" style={{ borderTop: '1px solid var(--border-color)' }}>
          <span className="text-xs font-semibold uppercase tracking-wider opacity-35 mr-1" style={{ color: 'var(--text-color)' }}>Filtrar:</span>
          <div className="min-w-[130px]">
            <CheckboxMultiSelect options={usersOptions} value={selectedUsers} onChange={onUsersChange} placeholder="Usuarios" />
          </div>
          <div className="min-w-[110px]">
            <CheckboxMultiSelect options={mesasOptions} value={selectedMesas} onChange={onMesasChange} placeholder="Mesas" />
          </div>
          <div className="min-w-[110px]">
            <CheckboxMultiSelect options={tipoOptions} value={selectedTipo} onChange={onTipoChange} placeholder="Tipo" />
          </div>
          <div className="min-w-[130px]">
            <CheckboxMultiSelect options={productosOptions} value={selectedProductos} onChange={onProductosChange} placeholder="Productos" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterBar;
